import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const statusConfig = {
  loading: {
    label: "Verifying",
    title: ["Checking your", "transaction…"],
    color: "#6c63ff",
    chipBg: "rgba(108,99,255,0.1)",
    chipBorder: "rgba(108,99,255,0.2)",
    chipColor: "#9d97ff",
    iconBg: "rgba(108,99,255,0.1)",
    glowColor: "#6c63ff",
    spin: true,
  },
  success: {
    label: "Confirmed",
    title: ["Payment", "Successful"],
    color: "#00e5a0",
    chipBg: "rgba(0,229,160,0.1)",
    chipBorder: "rgba(0,229,160,0.2)",
    chipColor: "#00e5a0",
    iconBg: "rgba(0,229,160,0.1)",
    glowColor: "#00e5a0",
    spin: false,
    btnLabel: "Done",
    btnStyle: { background: "#00e5a0", color: "#0a0a0f" },
  },
  failed: {
    label: "Failed",
    title: ["Payment", "Failed"],
    color: "#ff4d6d",
    chipBg: "rgba(255,77,109,0.1)",
    chipBorder: "rgba(255,77,109,0.2)",
    chipColor: "#ff4d6d",
    iconBg: "rgba(255,77,109,0.1)",
    glowColor: "#ff4d6d",
    spin: false,
    btnLabel: "Try Again",
    btnStyle: {
      background: "rgba(255,77,109,0.1)",
      color: "#ff4d6d",
      border: "1px solid rgba(255,77,109,0.25)",
    },
  },
};

const CheckIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="10" fill="rgba(0,229,160,0.15)" />
    <path d="M11 16.5 L14.5 20 L21 13" stroke="#00e5a0" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="10" fill="rgba(255,77,109,0.12)" />
    <path d="M12 12 L20 20 M20 12 L12 20" stroke="#ff4d6d" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <circle cx="16" cy="16" r="10" stroke="rgba(108,99,255,0.4)" strokeWidth="2" />
    <path d="M16 10 L16 16 L20 20" stroke="#9d97ff" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const icons = { success: <CheckIcon />, failed: <XIcon />, loading: <ClockIcon /> };

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Mono:wght@300;400;500&display=swap');

  .pr-fullscreen {
    position: fixed;
    inset: 0;
    background: #0a0a0f;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  }

  .pr-scene { position: relative; width: 525px; }

  .pr-glow {
    position: absolute; inset: -60px; border-radius: 50%;
    filter: blur(80px); opacity: 0.35; pointer-events: none;
    transition: background 0.8s ease;
  }

  .pr-card {
    position: relative;
    background: #13131c;
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 24px;
    padding: 48px 40px 40px;
    overflow: hidden;
    animation: prSlideUp 0.6s cubic-bezier(0.16,1,0.3,1) both;
  }

  @keyframes prSlideUp {
    from { opacity: 0; transform: translateY(30px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes prSpinRing { to { transform: rotate(360deg); } }

  @keyframes prPulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.7); }
  }

  @keyframes prSkeleton {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  .pr-icon-wrap {
    width: 72px; height: 72px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 28px; position: relative;
  }

  .pr-icon-ring {
    position: absolute; inset: -4px; border-radius: 50%;
    border: 1.5px solid transparent;
  }

  /* Spin only when .spinning class is present */
  .pr-icon-ring.spinning {
    animation: prSpinRing 2s linear infinite;
  }

  .pr-h1 {
    font-size: 26px; font-weight: 800;
    letter-spacing: -0.02em; line-height: 1.2; margin-bottom: 12px; color: #f0f0f8;
  }

  .pr-msg {
    font-family: 'DM Mono', monospace; font-size: 13px; font-weight: 300;
    color: rgba(255,255,255,0.45); line-height: 1.6; margin-bottom: 0;
  }

  .pr-divider { height: 1px; background: rgba(255,255,255,0.05); margin-bottom: 24px; margin-top: 12px; }

  .pr-skeleton {
    height: 14px; border-radius: 6px; margin-bottom: 10px;
    background: linear-gradient(90deg,rgba(255,255,255,0.04) 25%,rgba(255,255,255,0.08) 50%,rgba(255,255,255,0.04) 75%);
    background-size: 200% 100%; animation: prSkeleton 1.5s infinite;
  }
`;

const PayoutResult = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        const res = await fetch(
          "https://api.e-pocketgateway.com/v1/api/payments/get-transaction-status",
          {
            method: "POST",
            headers: { "Content-Type": "application/json", accept: "*/*" },
            body: JSON.stringify({ setting: { source: "EPG15_PS2S" }, transaction_id: id }),
          }
        );
        const data = await res.json();
        if (data?.data?.status === true) {
          setStatus("success");
          setMessage(data?.data?.data?.message || "Transaction confirmed.");
        } else {
          setStatus("failed");
          setMessage(data?.data?.error_message || "Transaction failed.");
        }
      } catch {
        setStatus("failed");
        setMessage("Something went wrong. Please try again.");
      }
    };
    if (id) fetchTransaction();
  }, [id]);

  const cfg = statusConfig[status];

  return (
    <>
      <style>{css}</style>
      <div className="pr-fullscreen">
        <div className="pr-scene">
          {/* Ambient glow */}
          <div
            className="pr-glow"
            style={{ background: `radial-gradient(circle, ${cfg.glowColor} 0%, transparent 70%)` }}
          />

          {/* Card */}
          <div className="pr-card">
            {/* Top accent line */}
            <div style={{
              position: "absolute", top: 0, left: 0, right: 0, height: 2,
              borderRadius: "24px 24px 0 0",
              background: `linear-gradient(90deg, transparent, ${cfg.color}, transparent)`
            }} />

            {/* Icon */}
            <div className="pr-icon-wrap" style={{ background: cfg.iconBg }}>
              <div
                className={`pr-icon-ring${cfg.spin ? " spinning" : ""}`}
                style={ cfg.spin ? {
                  borderTopColor: cfg.color,
                  borderRightColor: `${cfg.color}55`,
                } : undefined}
              />
              {icons[status]}
            </div>

            {/* Title */}
            <h1 className="pr-h1">{cfg.title[0]} {cfg.title[1]}</h1>

            <div className="pr-divider" />

            {/* Transaction ID */}
            <p className="pr-msg">Transaction ID : {id || "—"}</p>

            {/* Message / skeleton */}
            {status === "loading" ? (
              <div style={{ marginTop: 16 }}>
                <div className="pr-skeleton" style={{ width: "80%" }} />
                <div className="pr-skeleton" style={{ width: "60%" }} />
              </div>
            ) : (
              <p className="pr-msg" style={{ marginTop: 8 }}>Message : {message}</p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PayoutResult;